<template>
  <v-container fluid fill-height class="bm-page1" pa-0>
    <div
      class="flex-main-scroll d-flex flex-column"
      style="
        height: 100%;
        flex: 1 1 auto;
        width: 100%;
        min-height: 300px;
        overflow: auto;
      "
    >
      <div
        class="bm-pagehead pa-3"
        style="flex: 0 0 auto"
        :class="{
          'bm-grid-controls': hasGridControls,
          'pa-6': screenSize.smAndUp,
        }"
      >
        <v-layout wrap justify-end align-center>
          <v-flex no-flex>
            <slot name="breadcrumbs">
              <bm-breadcrumbs
                :parent-route="parentRoute"
                :parent-route-location="parentRouteLocation"
              />
            </slot>
            <slot name="title">
              <h1 v-if="parentRouteLocation" data-qa="page_title">
                {{ title }}
                <router-link
                  :to="parentRouteLocation"
                  :target="openLocationInNewTab ? '_blank' : '_self'"
                >
                  {{ titleWithRoute }}
                </router-link>
                <v-progress-circular
                  v-if="isLoading && !$slots['subtitle']"
                  size="28"
                  width="3"
                  class="my-auto"
                  data-qa="page-lazy-loader"
                  indeterminate
                />
              </h1>
              <h1 v-else data-qa="page_title">
                {{ title }}
                <v-progress-circular
                  v-if="isLoading && !$slots['subtitle']"
                  size="28"
                  width="3"
                  class="my-auto"
                  data-qa="page-lazy-loader"
                  indeterminate
                />
              </h1>
            </slot>
            <div class="d-flex">
              <slot name="subtitle">
                <div v-if="subtitle" data-qa="page_subtitle">
                  <span v-if="subtitleLocation">
                    <router-link
                      :to="subtitleLocation"
                      :target="openLocationInNewTab ? '_blank' : '_self'"
                    >
                      {{ subtitle }}
                    </router-link>
                  </span>
                  <span v-else>
                    {{ subtitle }}
                  </span>
                </div>
              </slot>
              <v-progress-circular
                v-if="isLoading && $slots['subtitle']"
                size="14"
                width="2"
                class="ml-2 my-auto"
                indeterminate
              />
            </div>
          </v-flex>
          <v-spacer />
          <v-flex :shrink="shrinkStatus" class="bm-heading2 pt-2">
            <slot name="status">
              <div v-if="status" data-qa="page_status">
                {{ status }}
              </div>
            </slot>
            <slot name="grid-controls">
              <portal-target name="grid-controls" />
            </slot>
          </v-flex>
        </v-layout>
        <v-divider class="bm-grid-divider" />
      </div>
      <div
        ref="contentRef"
        class="grid-page-content"
        style="display: flex; flex-flow: column; flex: 1 1 0; min-height: 0"
        :class="{
          'px-3': !removePagePadding,
          'px-6': screenSize.smAndUp && !removePagePadding,
        }"
      >
        <slot />
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import BmBreadcrumbs from "@/components/controls/app/BmBreadcrumbs.vue";
import appStore from "@/store/modules/appStore";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  watch,
} from "vue";
import type { Location } from "vue-router";
import { Wormhole } from "portal-vue";
import debounce from "lodash-es/debounce";
import { screenSize } from "@/vue23switching/screenSize";

interface Props {
  title?: string;
  subtitle?: string;
  subtitleLocation?: Location | null;
  openLocationInNewTab?: boolean;
  status?: string;
  parentRoute?: string | null;
  removePagePadding?: boolean;
  isLoading?: boolean;
  shrinkStatus?: boolean;
  parentRouteLocation?: Location | null;
  titleWithRoute?: string;
}

interface Events {
  (event: "content-height-resized", height: number): void;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  subtitle: "",
  subtitleLocation: null,
  openLocationInNewTab: false,
  status: "",
  parentRoute: null,
  removePagePadding: false,
  isLoading: false,
  shrinkStatus: true,
  parentRouteLocation: null,
  titleWithRoute: "",
});

const emit = defineEmits<Events>();

const contentRef = ref<HTMLElement | null>(null);
const slots = useSlots();

const hasGridControls = computed(() => {
  return (
    slots["grid-controls"] !== undefined || Wormhole.hasTarget("grid-controls")
  );
});

watch(
  () => props.title,
  (title) => {
    appStore.setDocumentTitle(title);
  },
  { immediate: true }
);

onMounted(() => {
  const contentElement = contentRef.value;
  if (!contentElement) {
    return;
  }

  const emitContentHeight = debounce((height: number) => {
    emit("content-height-resized", height);
  }, 300);

  const resizeObserver = new ResizeObserver((entries) => {
    const height = entries[0]?.contentRect.height ?? 0;
    if (height > 0) {
      emitContentHeight(height);
    }
  });

  resizeObserver.observe(contentElement);

  onBeforeUnmount(() => {
    resizeObserver.disconnect();
    emitContentHeight.cancel();
  });
});
</script>

<style scoped lang="sass">
.fit-content
  min-width: fit-content

// top and left/right padding to match vue container
.bm-pagehead
  padding-top: 12px
  padding-left: 12px
  padding-right: 12px

.bm-pagehead:not(.bm-grid-controls)
  margin-bottom: 20px

// Move data filters closer to the header as they have a lot of whitespace at the top.
.bm-grid-data-filter
  margin-top: -20px

.bm-grid-divider
  margin-bottom: 10px

@media print
// reduce padding on print
 .grid-page-content
    padding: 0 !important
</style>
